import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, ExternalLink } from "lucide-react";
import { Reveal, Overline } from "@/components/motion";
import Breadcrumbs from "@/components/Breadcrumbs";
import QuoteDialog from "@/components/QuoteDialog";
import Seo from "@/components/Seo";
import { MILLET_PAGES } from "@/data/milletContent";
import { absoluteUrl, breadcrumbSchema, faqSchema, COMPANY } from "@/lib/seo";

function contentSchema(page) {
  const isArticle = page.type === "article";
  return {
    "@context": "https://schema.org",
    "@type": isArticle ? "Article" : "WebPage",
    "@id": `${absoluteUrl(page.path)}#${isArticle ? "article" : "webpage"}`,
    headline: page.h1,
    description: page.description,
    url: absoluteUrl(page.path),
    image: page.image,
    ...(isArticle ? {
      author: { "@type": "Organization", name: COMPANY.name, url: absoluteUrl("/") },
      publisher: { "@type": "Organization", name: COMPANY.name, url: absoluteUrl("/") },
    } : {}),
    inLanguage: "en",
  };
}

function Section({ section }) {
  return (
    <section className="border-b border-brand-line py-10 last:border-b-0">
      <h2 className="font-display text-3xl md:text-4xl font-medium text-brand-green">{section.heading}</h2>
      {section.paragraphs?.map((paragraph) => (
        <p key={paragraph} className="text-brand-muted leading-relaxed mt-5 max-w-3xl">{paragraph}</p>
      ))}
      {section.list && (
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          {section.list.map((item) => (
            <li key={item} className="flex gap-3 text-brand-charcoal leading-relaxed">
              <Check size={18} className="text-brand-gold-dark shrink-0 mt-1" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      )}
      {section.countryList && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6">
          {section.countryList.map((country) => (
            <div key={country} className="bg-brand-cream border border-brand-line px-5 py-4 text-brand-charcoal font-medium">{country}</div>
          ))}
        </div>
      )}
      {section.process && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7">
          {section.process.map((item, index) => (
            <div key={item.title} className="bg-brand-cream border border-brand-line p-6">
              <span className="text-xs uppercase tracking-[0.18em] text-brand-gold-dark font-semibold">0{index + 1}</span>
              <h3 className="font-display text-2xl text-brand-green mt-3">{item.title}</h3>
              <p className="text-sm text-brand-muted leading-relaxed mt-2">{item.text}</p>
            </div>
          ))}
        </div>
      )}
      {section.table && (
        <div className="overflow-x-auto mt-7 border border-brand-line">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-green text-brand-cream">
              <tr>{section.table.headers.map((header) => <th key={header} className="px-5 py-4 font-semibold">{header}</th>)}</tr>
            </thead>
            <tbody>
              {section.table.rows.map((row) => (
                <tr key={row[0]} className="border-t border-brand-line">
                  {row.map((cell) => <td key={cell} className="px-5 py-4 text-brand-charcoal">{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function MilletContent({ pageKey }) {
  const page = MILLET_PAGES[pageKey];
  if (!page) return null;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Millet Export", path: "/millet-export" },
    ...(pageKey !== "pillar" ? [{ name: page.h1, path: page.path }] : []),
  ];
  const schemas = [breadcrumbSchema(crumbs), contentSchema(page)];
  if (page.faqs?.length) schemas.push(faqSchema(page.faqs));

  return (
    <div>
      <Seo title={page.title} description={page.description} path={page.path} image={page.image} type={page.type === "article" ? "article" : "website"} jsonLd={schemas} />
      <section className="pt-36 md:pt-44 pb-16 md:pb-24 bg-brand-green-dark">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><Breadcrumbs items={crumbs} className="mb-8" /></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Reveal><Overline className="text-brand-gold-light mb-5">{page.eyebrow}</Overline></Reveal>
              <Reveal delay={0.04}><h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-brand-cream leading-none">{page.h1}</h1></Reveal>
              <Reveal delay={0.08}><p className="mt-7 text-lg md:text-xl text-brand-cream/75 leading-relaxed max-w-3xl">{page.intro}</p></Reveal>
            </div>
            <Reveal delay={0.12} className="lg:col-span-4">
              <img src={page.image} alt={page.imageAlt} loading="eager" decoding="async" className="w-full aspect-[4/3] object-cover rounded-sm" />
            </Reveal>
          </div>
        </div>
      </section>

      <main className="bg-white py-12 md:py-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            {page.sections.map((section) => <Section key={section.heading} section={section} />)}

            {page.faqs?.length > 0 && (
              <section className="py-10" data-testid="millet-faqs">
                <h2 className="font-display text-3xl md:text-4xl font-medium text-brand-green">Frequently asked questions</h2>
                <div className="mt-6 space-y-5">
                  {page.faqs.map((faq) => (
                    <div key={faq.q} className="border-b border-brand-line pb-5">
                      <h3 className="font-display text-2xl text-brand-green">{faq.q}</h3>
                      <p className="text-brand-muted leading-relaxed mt-2">{faq.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="border-t border-brand-line pt-8 mt-4">
              <h2 className="font-display text-2xl text-brand-green">Sources and further reading</h2>
              <p className="text-sm text-brand-muted mt-2">Check current official guidance and destination requirements before placing an order.</p>
              <div className="flex flex-wrap gap-x-6 gap-y-3 mt-5">
                {page.sources.map((source) => <a key={source.href} href={source.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-green hover:text-brand-gold-dark transition-colors">{source.label}<ExternalLink size={13} /></a>)}
              </div>
            </section>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28 space-y-8">
              <div className="bg-brand-cream border border-brand-line p-7">
                <Overline className="text-brand-gold-dark">Explore the cluster</Overline>
                <nav className="mt-5 space-y-3">
                  {page.related.map((link) => <Link key={link.to} to={link.to} className="flex items-start gap-2 text-sm font-medium text-brand-green hover:text-brand-gold-dark transition-colors"><ArrowRight size={15} className="mt-0.5 shrink-0" />{link.label}</Link>)}
                </nav>
              </div>
              <div className="bg-brand-green p-7 text-brand-cream">
                <h2 className="font-display text-3xl">Discuss your requirement.</h2>
                <p className="text-sm text-brand-cream/75 mt-3 leading-relaxed">Share the product, volume, destination and specification with the SDRC Global team.</p>
                <div className="mt-6 flex flex-col gap-3">
                  <QuoteDialog><button className="bg-brand-gold text-brand-green-dark px-5 py-3 rounded-full text-sm font-semibold hover:bg-brand-gold-light transition-colors">Request a Quote</button></QuoteDialog>
                  <Link to="/contact" className="text-sm text-center border border-brand-cream/40 px-5 py-3 rounded-full hover:bg-brand-cream hover:text-brand-green transition-colors">Contact SDRC Global</Link>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
